// Visual Tools Template Gallery Data
// Professional design with Lucide icons

export const VISUAL_TEMPLATES = {
  // Table templates
  table: {
    title: 'Select Table Style',
    description: 'What kind of table would you like to create?',
    templates: [
      {
        id: 'compare',
        name: 'Comparison',
        icon: 'Scale',
        description: 'Compare and analyze items',
        gradient: 'from-cyan-500 to-blue-500',
        prompt: 'Create a comparison table',
        examples: ['Samsung vs SK Hynix 2024 earnings comparison', 'iPhone vs Galaxy specs comparison'],
      },
      {
        id: 'timeline',
        name: 'Trend',
        icon: 'TrendingUp',
        description: 'Track changes over time',
        gradient: 'from-blue-500 to-indigo-500',
        prompt: 'Organize trends over time in a table',
        examples: ['Quarterly revenue trend', 'Year-over-year growth rate changes'],
      },
      {
        id: 'rank',
        name: 'Ranking',
        icon: 'Trophy',
        description: 'Rankings and leaderboards',
        gradient: 'from-amber-500 to-orange-500',
        prompt: 'Create a ranking table',
        examples: ['Top 10 market cap companies', 'Brand value rankings'],
      },
      {
        id: 'specs',
        name: 'Specs',
        icon: 'ClipboardList',
        description: 'Detailed specifications',
        gradient: 'from-emerald-500 to-teal-500',
        prompt: 'Organize specs in a table',
        examples: ['Product specifications', 'Service plan comparison'],
      },
    ],
  },

  // Chart templates
  chart: {
    title: 'Select Chart Type',
    description: 'What kind of chart would you like?',
    templates: [
      {
        id: 'bar',
        name: 'Bar Chart',
        icon: 'BarChart3',
        description: 'Compare values across categories',
        gradient: 'from-violet-500 to-purple-500',
        prompt: 'Visualize with a bar chart',
        examples: ['Quarterly revenue comparison', 'Department performance comparison'],
      },
      {
        id: 'line',
        name: 'Line Graph',
        icon: 'LineChart',
        description: 'Analyze trends over time',
        gradient: 'from-blue-500 to-cyan-500',
        prompt: 'Show trends with a line graph',
        examples: ['Stock price trend', 'Monthly visitor count changes'],
      },
      {
        id: 'pie',
        name: 'Pie Chart',
        icon: 'PieChart',
        description: 'Proportions and market share',
        gradient: 'from-pink-500 to-rose-500',
        prompt: 'Show proportions with a pie chart',
        examples: ['Market share', 'Budget allocation'],
      },
      {
        id: 'combo',
        name: 'Combo Chart',
        icon: 'Activity',
        description: 'Bar + line graph combined',
        gradient: 'from-indigo-500 to-violet-500',
        prompt: 'Analyze with a combo chart',
        examples: ['Revenue and growth rate comparison', 'Sales volume and market share analysis'],
      },
    ],
  },

  // Infographic templates
  infographic: {
    title: 'Infographic Style',
    description: 'How would you like to visualize this?',
    templates: [
      {
        id: 'stats',
        name: 'Statistics',
        icon: 'Hash',
        description: 'Number-focused highlights',
        gradient: 'from-orange-500 to-amber-500',
        prompt: 'Create a statistics infographic',
        examples: ['Annual performance key figures', 'Key KPI summary'],
      },
      {
        id: 'process',
        name: 'Process',
        icon: 'GitBranch',
        description: 'Step-by-step flow',
        gradient: 'from-emerald-500 to-green-500',
        prompt: 'Organize a process infographic',
        examples: ['Investment process', 'Product development stages'],
      },
      {
        id: 'comparison',
        name: 'VS Comparison',
        icon: 'ArrowLeftRight',
        description: 'A vs B head-to-head',
        gradient: 'from-red-500 to-orange-500',
        prompt: 'Create a comparison infographic',
        examples: ['EV vs combustion engine', 'Cloud service comparison'],
      },
      {
        id: 'icons',
        name: 'Icon-based',
        icon: 'Sparkles',
        description: 'Icon-centered explanation',
        gradient: 'from-purple-500 to-pink-500',
        prompt: 'Create an icon infographic',
        examples: ['Company core values', 'Service feature summary'],
      },
    ],
  },

  // Image generation templates
  image: {
    title: 'Select Image Style',
    description: 'AI image generation style',
    templates: [
      {
        id: 'realistic',
        name: 'Realistic',
        icon: 'Camera',
        description: 'Photorealistic image',
        gradient: 'from-gray-600 to-gray-800',
        prompt: 'Generate a realistic image',
        examples: ['Future cityscape', 'Advanced tech product'],
      },
      {
        id: 'illustration',
        name: 'Illustration',
        icon: 'Palette',
        description: 'Graphic art style',
        gradient: 'from-fuchsia-500 to-pink-500',
        prompt: 'Draw in illustration style',
        examples: ['Business concept diagram', 'Technology explainer image'],
      },
      {
        id: 'abstract',
        name: 'Abstract',
        icon: 'Hexagon',
        description: 'Concept art style',
        gradient: 'from-violet-500 to-purple-600',
        prompt: 'Express as an abstract image',
        examples: ['Innovation concept', 'AI/Technology symbol'],
      },
      {
        id: 'minimal',
        name: 'Minimal',
        icon: 'Circle',
        description: 'Simple and clean',
        gradient: 'from-slate-500 to-slate-700',
        prompt: 'Create a minimal image',
        examples: ['Logo concept', 'Icon design'],
      },
    ],
  },

  // Timeline templates
  timeline: {
    title: 'Timeline Type',
    description: 'What kind of timeline would you like?',
    templates: [
      {
        id: 'history',
        name: 'History',
        icon: 'History',
        description: 'Company or person history',
        gradient: 'from-amber-600 to-yellow-500',
        prompt: 'Create a history timeline',
        examples: ['Samsung semiconductor history', 'Tesla growth timeline'],
      },
      {
        id: 'project',
        name: 'Project',
        icon: 'Target',
        description: 'Project schedule',
        gradient: 'from-teal-500 to-cyan-500',
        prompt: 'Organize a project timeline',
        examples: ['New product launch schedule', 'Service launch plan'],
      },
      {
        id: 'event',
        name: 'Events',
        icon: 'CalendarDays',
        description: 'Key events summary',
        gradient: 'from-blue-500 to-indigo-500',
        prompt: 'Show key events on a timeline',
        examples: ['2024 economic events', 'Industry conference schedule'],
      },
      {
        id: 'forecast',
        name: 'Forecast',
        icon: 'Telescope',
        description: 'Future prediction timeline',
        gradient: 'from-purple-500 to-violet-500',
        prompt: 'Create a future forecast timeline',
        examples: ['AI technology development outlook', 'EV market prediction'],
      },
    ],
  },

  // Map templates
  map: {
    title: 'Select Map Type',
    description: 'What kind of map would you like?',
    templates: [
      {
        id: 'distribution',
        name: 'Distribution',
        icon: 'Map',
        description: 'Regional distribution status',
        gradient: 'from-lime-500 to-green-500',
        prompt: 'Create a regional distribution map',
        examples: ['Nationwide store locations', 'Global subsidiary overview'],
      },
      {
        id: 'heatmap',
        name: 'Heatmap',
        icon: 'Flame',
        description: 'Density and concentration',
        gradient: 'from-red-500 to-orange-500',
        prompt: 'Visualize as a heatmap',
        examples: ['Population density areas', 'Real estate price distribution'],
      },
      {
        id: 'route',
        name: 'Route',
        icon: 'Route',
        description: 'Movement routes',
        gradient: 'from-blue-500 to-teal-500',
        prompt: 'Show routes on a map',
        examples: ['Logistics network', 'Tourist itinerary'],
      },
      {
        id: 'cluster',
        name: 'Cluster',
        icon: 'MapPin',
        description: 'Group location markers',
        gradient: 'from-yellow-500 to-amber-500',
        prompt: 'Create a cluster map',
        examples: ['Industrial park locations', 'R&D center distribution'],
      },
    ],
  },
};

// Check if tool is a visual tool
export const isVisualTool = (toolId) => {
  return ['table', 'chart', 'infographic', 'image', 'timeline', 'map'].includes(toolId);
};

// Get templates for a tool
export const getTemplatesForTool = (toolId) => {
  return VISUAL_TEMPLATES[toolId] || null;
};

export default VISUAL_TEMPLATES;
